import { useQuery } from '@tanstack/react-query'
import { api } from '../../lib/api'
import { formatDate, titleCase } from '../../lib/format'
import { Alert, Card, CardBody, CardHeader, EmptyState, Skeleton } from '../../components/ui'
import { IdIcon } from '../../components/icons'

/* Port of admin/users_map.html (admin/routes.py users_map()) - every user's
 * last-known GPS location. The Jinja original plots these on a live map;
 * this app has no mapping library available yet, so this is a table with a
 * "View on map" link per row (opens Google Maps in a new tab) rather than
 * an embedded map - same data, lighter dependency. Reuses the already
 * platform-wide-for-super_admin /leader/reports/user-locations endpoint
 * (built for the leader portal's own location report, allowed_uids stays
 * unrestricted for super_admin/gov_director). */

interface LocationRow {
  uid: string
  name: string
  email: string
  role: string
  lat: number
  lng: number
  accuracy_m?: number | null
  recorded_at?: string
}

interface LocationsResponse { ok: boolean; items: LocationRow[] }

export default function AdminUsersMap() {
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['admin', 'users-map'],
    queryFn: () => api.get<LocationsResponse>('/leader/reports/user-locations'),
  })

  const items = data?.items ?? []

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Users map</h1>
          <p className="page-head__sub">Last-known location for every user who has shared one.</p>
        </div>
      </div>

      <Card>
        <CardHeader title="Locations" sub={items.length ? `${items.length} user(s)` : undefined} />
        <CardBody flush>
          {isError ? (
            <div style={{ padding: 'var(--sp-6)' }}><Alert tone="danger">{(error as Error).message}</Alert></div>
          ) : isLoading ? (
            <div style={{ padding: 'var(--sp-6)' }} className="stack"><Skeleton h={20} /><Skeleton h={20} /></div>
          ) : items.length === 0 ? (
            <EmptyState icon={<IdIcon size={22} />} title="No locations shared yet" body="Users who share their location will appear here." />
          ) : (
            <div className="table-wrap">
              <table className="table">
                <thead><tr><th>Name</th><th>Role</th><th>Coordinates</th><th>Last seen</th><th /></tr></thead>
                <tbody>
                  {items.map((u) => (
                    <tr key={u.uid}>
                      <td style={{ fontWeight: 600 }}>{u.name}</td>
                      <td>{titleCase(u.role)}</td>
                      <td>{u.lat.toFixed(5)}, {u.lng.toFixed(5)}</td>
                      <td>{formatDate(u.recorded_at) || '—'}</td>
                      <td style={{ textAlign: 'right' }}>
                        <a href={`https://www.google.com/maps?q=${u.lat},${u.lng}`} target="_blank" rel="noreferrer">
                          View on map →
                        </a>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>
    </>
  )
}
